import { Injectable, TemplateRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  PublicationStatus,
  Shift,
  StartAssignmentOptimizerJobDto,
  TimelineTimeframe,
  UserTimelines,
  UserTimelinesWithoutActivitiesPayload,
} from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import {
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
} from '@wilson/shift-timeline/state';
import { areIntervalsOverlapping } from 'date-fns';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AssignAllShiftsButtonService {
  private translate = inject(TranslateService);
  private nzModalService = inject(NzModalService);
  private store = inject(Store);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );

  @WilsonState<boolean>(false)
  private isAssignmentOptimizerDrawerVisible!: boolean;
  public isAssignmentOptimizerDrawerVisible$!: Observable<boolean>;

  public renderAssignmentOptimizerDrawer(value: boolean): void {
    this.isAssignmentOptimizerDrawerVisible = value;
  }

  public confirmIncludesReleasedShifts(
    numberOfReleasedShifts: number,
  ): NzModalRef<unknown, unknown> {
    const translateString =
      numberOfReleasedShifts > 1
        ? 'page.shift_timeline.assign_all_shifts.multiple_released_shift.confirmation_modal.detail_text'
        : 'page.shift_timeline.assign_all_shifts.single_released_shift.confirmation_modal.detail_text';
    return this.nzModalService.confirm({
      nzTitle: this.translate.instant('shift.edit_popup.title'),
      nzContent: this.translate.instant(translateString, {
        number: numberOfReleasedShifts,
      }),
      nzOkText: this.translate.instant('general.ok'),
      nzCancelText: this.translate.instant('general.cancel'),
      nzOkType: 'primary',
      nzClosable: false,
      nzOkDanger: false,
      nzOnOk: () => true,
    });
  }

  initializeShiftAssignmentJob({
    visibleDateRange,
    shiftAssignments,
    absenceAssignments,
    unassignedShifts,
    isCellView,
  }: {
    visibleDateRange: TimelineTimeframe;
    shiftAssignments: Record<string, string[]>;
    absenceAssignments: Record<string, string[]>;
    unassignedShifts: string[];
    isCellView: boolean;
  }): StartAssignmentOptimizerJobDto | null {
    const timelineSettings = { isCellView };

    if (visibleDateRange.start && visibleDateRange.end) {
      return {
        startDateTime: new Date(visibleDateRange.start).toISOString(),
        endDateTime: new Date(visibleDateRange.end).toISOString(),
        shiftAssignments,
        absenceAssignments,
        unassignedShifts,
        jobId: crypto.randomUUID(),
        userAlgoSettings: {
          blockNightHours: false,
          ignoredQualifications: [],
          minimalRestTimeInHours: 0,
          maximalLengthOfTransfer: 0,
          reassignAll: false,
        },
        timelineSettings: JSON.stringify(timelineSettings),
      };
    } else {
      return null;
    }
  }

  public async mapUserShiftAndAbsenceAssignments({
    visibleDateRange,
    userTimelinesData,
  }: {
    visibleDateRange: TimelineTimeframe;
    userTimelinesData: Record<string, UserTimelinesWithoutActivitiesPayload>;
  }): Promise<{
    shiftAssignments: Record<string, string[]>;
    absenceAssignments: Record<string, string[]>;
  }> {
    if (!Object.keys(userTimelinesData).length) {
      return { shiftAssignments: {}, absenceAssignments: {} };
    } else {
      return Object.entries(userTimelinesData).reduce(
        (
          acc: {
            shiftAssignments: Record<string, string[]>;
            absenceAssignments: Record<string, string[]>;
          },
          [userId, { shifts, absences }],
        ): {
          shiftAssignments: Record<string, string[]>;
          absenceAssignments: Record<string, string[]>;
        } => {
          const filteredShifts = Object.values(shifts).reduce((acc, shift) => {
            const isOverlapping = areIntervalsOverlapping(
              {
                start: new Date(shift.startDatetime),
                end: new Date(shift.endDatetime),
              },
              {
                start: new Date(visibleDateRange.start),
                end: new Date(visibleDateRange.end),
              },
            );

            if (isOverlapping) {
              acc.push(shift.id);
            }
            return acc;
          }, [] as string[]);
          const filteredAbsences = Object.values(absences).reduce(
            (acc, absence) => {
              const isOverlapping = areIntervalsOverlapping(
                {
                  start: new Date(absence.absentFrom),
                  end: new Date(absence.absentTo),
                },
                {
                  start: visibleDateRange.start,
                  end: visibleDateRange.end,
                },
              );
              if (isOverlapping) {
                acc.push(absence.id);
              }
              return acc;
            },
            [] as string[],
          );
          acc.shiftAssignments[userId] = filteredShifts;
          acc.absenceAssignments[userId] = filteredAbsences;
          return acc;
        },
        { shiftAssignments: {}, absenceAssignments: {} } as {
          shiftAssignments: Record<string, string[]>;
          absenceAssignments: Record<string, string[]>;
        },
      );
    }
  }

  public async getNumberOfReleasedShiftWithoutActivitiesAssignmentsInView(): Promise<number> {
    const filteredUsers = await firstValueFrom(
      this.shiftTimelineFilterStateService.filteredUsers$,
    );
    const releasedShiftAssignments = new Set<string>();

    filteredUsers.forEach((user) => {
      Object.values(user.shiftsWithoutActivitiesDictionary).forEach((shift) => {
        if (this.isShiftReleased(shift, user)) {
          releasedShiftAssignments.add(shift.id);
        }
      });
    });

    return releasedShiftAssignments.size;
  }

  private isShiftReleased(
    shift: Shift & {
      id: string;
    },
    user: UserTimelines,
  ): boolean | string {
    return (
      shift.id &&
      user.id &&
      shift.publicationStatus === PublicationStatus.Published
    );
  }
}
